import { colors as themeColors } from '../utils/theme';
import { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Box, Typography, Snackbar, Alert, CircularProgress, LinearProgress, TextField, InputAdornment, Button, Dialog, DialogActions, List, ListItem, ListItemText, Paper, Chip, Divider, useMediaQuery, useTheme, } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaidIcon from '@mui/icons-material/Paid';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import { useAuth } from '../context/AuthContext';
import CenterLoader from './Test/components/CenterLoader';
import { APPBAR_HEIGHT, colors, gradients, radii, shadows } from '../utils/theme';


// ─── Design tokens ────────────────────────────────────────────────────────────
// Shared with the rest of the app's modern pages (Home2 / DashboardItems /
// ConfigureModule / UserDefineCharges): #1877F2 accent, #EEF1F6 hairlines,
// soft rgba(15,23,42,...) shadows, 14px section cards, 10px controls.

const C = {
    blue: colors.primary.main, blueDark: colors.primary.darker, blueTint: colors.primary.tint,
    green: colors.accent.green, greenDark: colors.accent.greenDark, greenTint: colors.accent.greenTint,
    amber: colors.warning.dark, amberTint: colors.warning.tint,
    ink: colors.neutral.ink, sub: colors.neutral.sub, muted: colors.neutral.muted,
    border: colors.neutral.border, borderStrong: colors.neutral.borderStrong, surface: colors.primary.surface,
};

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: `${radii.md}px`,
        bgcolor: colors.neutral.field,
        fontSize: '0.8rem',
        height: 36,
        transition: 'box-shadow .15s, background-color .15s',
        '& input': { padding: '0 12px', color: C.ink },
        '& input::placeholder': { color: C.muted, opacity: 1 },
        '& fieldset': { borderColor: C.borderStrong },
        '&:hover fieldset': { borderColor: C.blue },
        '&.Mui-focused': { bgcolor: themeColors.neutral.surface, boxShadow: '0 0 0 4px rgba(24,119,242,0.12)' },
        '&.Mui-focused fieldset': { borderColor: C.blue, borderWidth: 1.5 },
    },
};

const SearchableField = ({ value, onChange, placeholder, connectionString, companyId, bookType, linked }) => {
    const [inputValue, setInputValue] = useState(value || '');
    const [options, setOptions]       = useState([]);
    const [open, setOpen]             = useState(false);
    const [busy, setBusy]             = useState(false);
    const [position, setPosition]     = useState({ top: 0, left: 0, width: 0 });
    const [isUserTyping, setIsUserTyping] = useState(false); // track if user is actively typing
    const debounceRef                 = useRef(null);
    const selectingRef                = useRef(false);
    const inputRef                    = useRef(null);

    useEffect(() => { setInputValue(value || ''); }, [value]);

    // Calculate dropdown position
    const updatePosition = () => {
        if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            setPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    };

    useEffect(() => {
        if (open) {
            updatePosition();
            let frame = null;
            const scheduleUpdate = () => {
                if (frame) return;
                frame = requestAnimationFrame(() => {
                    frame = null;
                    updatePosition();
                });
            };
            window.addEventListener('scroll', scheduleUpdate, true);
            window.addEventListener('resize', scheduleUpdate);
            return () => {
                window.removeEventListener('scroll', scheduleUpdate, true);
                window.removeEventListener('resize', scheduleUpdate);
                if (frame) cancelAnimationFrame(frame);
            };
        }
    }, [open]);

    useEffect(() => {
        if (selectingRef.current) { selectingRef.current = false; return; }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setBusy(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const res = await axios.get(`${connectionString}/api/company-search`, {
                    params: { field: 'FirmName', search: inputValue.trim() || '', companyId },
                });
                const data = Array.isArray(res.data.data) ? res.data.data : [];
                const list = data.map(i => ({
                    id: i.id || i.ClientId,
                    clientname: i.FirmName || i.companyName || '',
                    City: i.City || '',
                    ContactPerson: i.ContactPerson || '',
                    MobileNo: i.MobileNo || '',
                    EmailId: i.EmailId || '',
                    GSTNo: i.GSTNo || ''
                }));
                setOptions(list);
                // Only show dropdown if user is actively typing
                setOpen(isUserTyping && list.length > 0 && inputValue.trim() !== '');
            } catch { setOptions([]); setOpen(false); }
            finally { setBusy(false); }
        }, 500);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [inputValue, connectionString, companyId, bookType, isUserTyping]);

    const handleChange = (e) => {
        setInputValue(e.target.value);
        onChange(e.target.value);
        setIsUserTyping(true);
        selectingRef.current = false;
    };

    const handleSelect = (item) => {
        selectingRef.current = true;
        setInputValue(item.clientname || '');
        onChange({ id: item.id || '', name: item.clientname || '' });
        setOptions([]);
        setOpen(false);
        setIsUserTyping(false);
    };

    const handleInputFocus = () => {
        updatePosition();
        setIsUserTyping(false); // focus alone doesn't trigger typing
    };

    const handleInputBlur = () => {
        setIsUserTyping(false);
        setOpen(false); // close dropdown on blur
    };

    return (
        <>
            <Box ref={inputRef} sx={{ position: 'relative', width: '100%' }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={inputValue}
                    onChange={handleChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder={placeholder}
                    InputProps={{
                        endAdornment: busy
                            ? <InputAdornment position="end"><CircularProgress size={13} sx={{ color: C.blue }} /></InputAdornment>
                            : (linked ? <InputAdornment position="end"><CheckCircleIcon sx={{ fontSize: 16, color: C.green }} /></InputAdornment> : null)
                    }}
                    sx={fieldSx}
                />
            </Box>

            {/* Portal-based dropdown to avoid z-index / overflow clipping */}
            {open && options.length > 0 && ReactDOM.createPortal(
                <Box
                    sx={{
                        position: 'fixed',
                        top: `${position.top}px`,
                        left: `${position.left}px`,
                        width: `${position.width}px`,
                        zIndex: 99999,
                        maxHeight: '250px',
                        overflowY: 'auto'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Paper elevation={0} sx={{ mt: 0.5, borderRadius: '12px', border: `1px solid ${C.borderStrong}`, boxShadow: '0 12px 32px rgba(15,23,42,0.14)', overflow: 'hidden' }}>
                        <List dense disablePadding>
                            {options.map((item, idx) => (
                                <ListItem
                                    key={item.id || idx}
                                    button
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleSelect(item)}
                                    sx={{
                                        py: 1,
                                        px: 1.5,
                                        cursor: 'pointer',
                                        borderBottom: idx < options.length - 1 ? `1px solid ${C.border}` : 'none',
                                        '&:hover': { bgcolor: C.blueTint },
                                        bgcolor: themeColors.neutral.surface,
                                        transition: 'background-color 0.15s ease'
                                    }}>
                                    <ListItemText
                                        primary={item.clientname}
                                        secondary={
                                            <Box component="span" sx={{ display: 'flex', gap: 1, fontSize: '0.66rem', color: C.sub, flexWrap: 'wrap', mt: 0.5 }}>
                                                {item.City && <span>📍 {item.City}</span>}
                                                {item.ContactPerson && <span>👤 {item.ContactPerson}</span>}
                                                {item.MobileNo && <span>📱 {item.MobileNo}</span>}
                                                {item.GSTNo && <span>🏢 {item.GSTNo}</span>}
                                            </Box>
                                        }
                                        primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: 600, color: C.ink }}
                                        secondaryTypographyProps={{ component: 'div' }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Box>,
                document.body
            )}

            {/* Close dropdown on outside click */}
            {open && <Box
                onClick={() => setOpen(false)}
                sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998 }}
            />}
        </>
    );
};


// ─── Group Panel (Bank or Cash) ───────────────────────────────────────────────

const LABEL_W = 190;   // px — fixed label column width on desktop
const ROW_MIN_W = 640; // px — min width for the label+receipt+payment grid before it scrolls instead of clipping

// Small coloured arrow badge used for the Receipt / Payment captions
const FlowTag = ({ kind, compact }) => {
    const isReceipt = kind === 'receipt';
    const color = isReceipt ? C.blue : C.amber;
    const bg = isReceipt ? C.blueTint : C.amberTint;
    const Arrow = isReceipt ? ArrowDownwardIcon : ArrowUpwardIcon;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
            <Box sx={{ width: compact ? 16 : 20, height: compact ? 16 : 20, borderRadius: '6px', bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Arrow sx={{ fontSize: compact ? 10 : 12, color }} />
            </Box>
            <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {isReceipt ? 'Receipt' : 'Payment'}
            </Typography>
        </Box>
    );
};

const GroupPanel = ({ title, subtitle, accent, accentDark, accentTint, icon, rows, colA, colB, onChangeA, onChangeB, connectionString, companyId, isMobile, bookTypeA, bookTypeB }) => {
    const linkedCount = rows.reduce((n, r) => n + (colA[`${r.key}Id`] ? 1 : 0) + (colB[`${r.key}Id`] ? 1 : 0), 0);
    const totalSlots = rows.length * 2;

    return (
        <Box sx={{ border: `1px solid ${C.border}`, borderRadius: '14px', overflow: 'hidden', bgcolor: themeColors.neutral.surface, boxShadow: shadows.card, flexShrink: 0 }}>

            {/* thin accent strip */}
            <Box sx={{ height: 3, background: `linear-gradient(90deg, ${accent} 0%, ${accentDark} 100%)` }} />

            {/* ── Panel header ─────────────────────────────── */}
            <Box sx={{ background: gradients.cardHeader, borderBottom: `1px solid ${C.border}`, px: { xs: 1.5, md: 2.25 }, py: 1.3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: '10px', bgcolor: accentTint, color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: C.ink, lineHeight: 1.25 }}>{title}</Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: C.sub }}>{subtitle}</Typography>
                    </Box>
                </Box>
                <Chip
                    label={`${linkedCount} of ${totalSlots} linked`}
                    size="small"
                    sx={{
                        height: 22, fontSize: '0.68rem', fontWeight: 700, borderRadius: '7px', flexShrink: 0,
                        bgcolor: linkedCount === totalSlots ? C.greenTint : accentTint,
                        color: linkedCount === totalSlots ? C.greenDark : accent,
                    }}
                />
            </Box>

            {/* ── Scrollable body: column headers + rows scroll together so nothing gets clipped ── */}
            <Box sx={{ overflowX: 'auto', overflowY: 'hidden', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
                <Box sx={{ minWidth: isMobile ? 'auto' : ROW_MIN_W }}>

                    {/* Column headers (desktop only) */}
                    {!isMobile && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: `${LABEL_W}px 1fr 1fr`, bgcolor: C.surface, borderBottom: `1px solid ${C.border}` }}>
                            <Box sx={{ px: 2, py: 0.9 }}>
                                <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, color: C.sub, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Account Head</Typography>
                            </Box>
                            <Box sx={{ px: 1.5, py: 0.9 }}><FlowTag kind="receipt" /></Box>
                            <Box sx={{ px: 1.5, py: 0.9 }}><FlowTag kind="payment" /></Box>
                        </Box>
                    )}

                    {/* Field rows */}
                    {rows.map((row, idx) => (
                        <Box key={row.key} sx={{
                            display: isMobile ? 'flex' : 'grid',
                            flexDirection: isMobile ? 'column' : undefined,
                            gridTemplateColumns: isMobile ? undefined : `${LABEL_W}px 1fr 1fr`,
                            alignItems: isMobile ? undefined : 'center',
                            borderBottom: idx < rows.length - 1 ? `1px solid ${C.border}` : 'none',
                            transition: 'background-color .15s',
                            '&:hover': { bgcolor: '#FAFCFF' },
                        }}>
                            {/* Label */}
                            <Box sx={{ px: 2, py: isMobile ? 1 : 1.2, display: 'flex', alignItems: 'center', gap: 1, bgcolor: isMobile ? C.surface : 'transparent' }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: accent, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: C.ink }}>{row.label}</Typography>
                            </Box>

                            {/* Receipt input */}
                            <Box sx={{ px: 1.5, py: isMobile ? 1 : 1.1 }}>
                                {isMobile && <Box sx={{ mb: 0.6 }}><FlowTag kind="receipt" compact /></Box>}
                                <SearchableField value={colA[`${row.key}Name`]} linked={!!colA[`${row.key}Id`]} onChange={(v) => onChangeA(row.key, v)} placeholder={row.placeholder} connectionString={connectionString} companyId={companyId} bookType={bookTypeA} />
                            </Box>

                            {/* Payment input */}
                            <Box sx={{ px: 1.5, py: isMobile ? 1 : 1.1 }}>
                                {isMobile && <Box sx={{ mb: 0.6 }}><FlowTag kind="payment" compact /></Box>}
                                <SearchableField value={colB[`${row.key}Name`]} linked={!!colB[`${row.key}Id`]} onChange={(v) => onChangeB(row.key, v)} placeholder={row.placeholder} connectionString={connectionString} companyId={companyId} bookType={bookTypeB} />
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};


// ─── Main Component ───────────────────────────────────────────────────────────

const BANK_ROWS = [
    { key: 'bankCommA', label: 'Bank Charges A/c', placeholder: 'e.g., BANK CHARGES' },
    { key: 'tdsA',      label: 'T.D.S. A/c',          placeholder: 'e.g., TDS A/C'      },
    { key: 'roundOffA', label: 'Round Off A/c',        placeholder: 'e.g., KASAR A/C'   },
];
const CASH_ROWS = [
    { key: 'tdsA',      label: 'T.D.S. A/c',    placeholder: 'e.g., TDS A/C'    },
    { key: 'roundOffA', label: 'Round Off A/c',  placeholder: 'e.g., KASAR A/C' },
];

export default function BankCashPosting({ connectionString }) {
    const { user, selectedCompany } = useAuth();
    const API       = `${connectionString}/api`;
    const userId    = user?.UserId || null;
    const companyId = selectedCompany?.CompId || null;

    const theme       = useTheme();
    const isMobile    = useMediaQuery(theme.breakpoints.down('md'));

    const [loading,     setLoading]     = useState(false);
    const [snackbar,    setSnackbar]    = useState({ open: false, message: '', severity: 'success' });
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [savedSnap,   setSavedSnap]   = useState(null); // last loaded / saved state, used to detect unsaved changes

    const [bankReceipt, setBankReceipt] = useState({ bankCommAId: null, bankCommAName: '', tdsAId: null, tdsAName: '', roundOffAId: null, roundOffAName: '' });
    const [bankPayment, setBankPayment] = useState({ bankCommAId: null, bankCommAName: '', tdsAId: null, tdsAName: '', roundOffAId: null, roundOffAName: '' });
    const [cashReceipt, setCashReceipt] = useState({ tdsAId: null, tdsAName: '', roundOffAId: null, roundOffAName: '' });
    const [cashPayment, setCashPayment] = useState({ tdsAId: null, tdsAName: '', roundOffAId: null, roundOffAName: '' });

    const currentSnap = JSON.stringify([bankReceipt, bankPayment, cashReceipt, cashPayment]);
    const isDirty = savedSnap !== null && currentSnap !== savedSnap;

    // ── Fetch ──────────────────────────────────────────────────────────────────

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res  = await axios.get(`${API}/BankCashGet`, { params: { companyId, userId } });
            const data = res.data || {};
            const bR = { bankCommAId: data.bankReceiptBankCommAId || null, bankCommAName: data.bankReceiptBankCommAName || '', tdsAId: data.bankReceiptTdsAId || null, tdsAName: data.bankReceiptTdsAName || '', roundOffAId: data.bankReceiptRoundOffAId || null, roundOffAName: data.bankReceiptRoundOffAName || '' };
            const bP = { bankCommAId: data.bankPaymentBankCommAId || null, bankCommAName: data.bankPaymentBankCommAName || '', tdsAId: data.bankPaymentTdsAId || null, tdsAName: data.bankPaymentTdsAName || '', roundOffAId: data.bankPaymentRoundOffAId || null, roundOffAName: data.bankPaymentRoundOffAName || '' };
            const cR = { tdsAId: data.cashReceiptTdsAId || null, tdsAName: data.cashReceiptTdsAName || '', roundOffAId: data.cashReceiptRoundOffAId || null, roundOffAName: data.cashReceiptRoundOffAName || '' };
            const cP = { tdsAId: data.cashPaymentTdsAId || null, tdsAName: data.cashPaymentTdsAName || '', roundOffAId: data.cashPaymentRoundOffAId || null, roundOffAName: data.cashPaymentRoundOffAName || '' };
            setBankReceipt(bR);
            setBankPayment(bP);
            setCashReceipt(cR);
            setCashPayment(cP);
            setSavedSnap(JSON.stringify([bR, bP, cR, cP]));
        } catch {
            setSnackbar({ open: true, message: 'Failed to load data.', severity: 'error' });
        } finally { setLoading(false); }
    }, [API, companyId, userId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ── Save ───────────────────────────────────────────────────────────────────

    const handleSave = async () => {
        setSaveLoading(true);
        try {
            await axios.post(`${API}/BankCashSave`, {
                companyId, userId, GCompId: selectedCompany?.GCompId || null,
                bankReceiptBankCommAId: bankReceipt.bankCommAId, bankReceiptBankCommAName: bankReceipt.bankCommAName,
                bankReceiptTdsAId: bankReceipt.tdsAId, bankReceiptTdsAName: bankReceipt.tdsAName,
                bankReceiptRoundOffAId: bankReceipt.roundOffAId, bankReceiptRoundOffAName: bankReceipt.roundOffAName,
                bankPaymentBankCommAId: bankPayment.bankCommAId, bankPaymentBankCommAName: bankPayment.bankCommAName,
                bankPaymentTdsAId: bankPayment.tdsAId, bankPaymentTdsAName: bankPayment.tdsAName,
                bankPaymentRoundOffAId: bankPayment.roundOffAId, bankPaymentRoundOffAName: bankPayment.roundOffAName,
                cashReceiptTdsAId: cashReceipt.tdsAId, cashReceiptTdsAName: cashReceipt.tdsAName,
                cashReceiptRoundOffAId: cashReceipt.roundOffAId, cashReceiptRoundOffAName: cashReceipt.roundOffAName,
                cashPaymentTdsAId: cashPayment.tdsAId, cashPaymentTdsAName: cashPayment.tdsAName,
                cashPaymentRoundOffAId: cashPayment.roundOffAId, cashPaymentRoundOffAName: cashPayment.roundOffAName,
            });
            setSavedSnap(currentSnap);
            setSnackbar({ open: true, message: 'Configuration saved successfully.', severity: 'success' });
            setConfirmOpen(false);
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Save failed.', severity: 'error' });
        } finally { setSaveLoading(false); }
    };

    // ── Change handlers ────────────────────────────────────────────────────────

    const makeHandler = (setter) => (field, val) => {
        if (typeof val === 'object' && val !== null && val.id !== undefined)
            setter(prev => ({ ...prev, [`${field}Id`]: val.id, [`${field}Name`]: val.name }));
        else
            setter(prev => ({ ...prev, [field]: val }));
    };

    const onBankReceipt = makeHandler(setBankReceipt);
    const onBankPayment = makeHandler(setBankPayment);
    const onCashReceipt = makeHandler(setCashReceipt);
    const onCashPayment = makeHandler(setCashPayment);

    // ── Render ─────────────────────────────────────────────────────────────────

    if (loading) return <CenterLoader />;

    const shared = { connectionString, companyId, isMobile };

    return (
        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', height: `calc(100vh - ${APPBAR_HEIGHT}px)`, background: gradients.page }}>

            {/* thin progress bar while saving */}
            {saveLoading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, zIndex: 5, bgcolor: 'rgba(24,119,242,0.15)', '& .MuiLinearProgress-bar': { bgcolor: C.blue } }} />}

            {/* ── Module header ──────────────────────────────────────────────── */}
            <Box sx={{
                background: gradients.primary,
                px: { xs: 1.5, md: 3 }, py: 1.3,
                boxShadow: '0 6px 16px rgba(24,119,242,0.22)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, flexShrink: 0,
            }}>
                {/* Left: title + breadcrumb */}
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Box sx={{ width: 28, height: 28, borderRadius: '9px', bgcolor: 'rgba(255,255,255,0.16)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <AccountBalanceIcon sx={{ fontSize: 15, color: themeColors.neutral.surface }} />
                        </Box>
                        <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.8rem', md: '0.88rem' }, color: themeColors.neutral.surface, letterSpacing: '0.1px' }}>
                            Bank / Cash A/c Configuration
                        </Typography>
                        <Chip label="FIN-BCP" size="small" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: 'rgba(255,255,255,0.18)', color: themeColors.neutral.surface, borderRadius: '6px', flexShrink: 0 }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mt: 0.3, ml: 0.3 }}>
                        {['Settings', 'Financial Config', 'Bank / Cash Posting'].map((crumb, i, arr) => (
                            <Box key={crumb} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography sx={{ fontSize: '0.66rem', color: i === arr.length - 1 ? themeColors.neutral.surface : 'rgba(255,255,255,0.6)', fontWeight: i === arr.length - 1 ? 700 : 400 }}>
                                    {crumb}
                                </Typography>
                                {i < arr.length - 1 && <NavigateNextIcon sx={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', mx: 0.2 }} />}
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Right: company pill */}
                {!isMobile && selectedCompany?.CompanyName && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, px: 1.2, py: 0.5, borderRadius: '999px', bgcolor: 'rgba(255,255,255,0.16)', maxWidth: 280 }}>
                        <BusinessOutlinedIcon sx={{ fontSize: 14, color: themeColors.neutral.surface }} />
                        <Typography noWrap sx={{ fontSize: '0.72rem', fontWeight: 700, color: themeColors.neutral.surface }}>{selectedCompany.CompanyName}</Typography>
                    </Box>
                )}
            </Box>

            {/* ── Scrollable body ────────────────────────────────────────────── */}
            <Box sx={{ flex: 1, overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', px: { xs: 1.5, md: 2.5 }, py: 2, minHeight: 0 }}>
                <Box sx={{ maxWidth: 1180, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>

                    {/* Intro line */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 0.5 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 16, color: C.blue, mt: '2px', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.76rem', color: C.sub, lineHeight: 1.55 }}>
                            Choose the default ledger accounts used when a <b style={{ color: C.ink }}>receipt</b> or <b style={{ color: C.ink }}>payment</b> is posted through bank or cash. Start typing to search your accounts.
                        </Typography>
                    </Box>

                    {/* Bank Transactions panel */}
                    <GroupPanel
                        title="Bank Transactions"
                        subtitle="Ledger accounts for bank-based entries"
                        accent={C.blue} accentDark={C.blueDark} accentTint={C.blueTint}
                        icon={<AccountBalanceIcon sx={{ fontSize: 17 }} />}
                        rows={BANK_ROWS}
                        colA={bankReceipt} colB={bankPayment}
                        onChangeA={onBankReceipt} onChangeB={onBankPayment}
                        bookTypeA="BKR" bookTypeB="BKP"
                        {...shared}
                    />

                    {/* Cash Transactions panel */}
                    <GroupPanel
                        title="Cash Transactions"
                        subtitle="Ledger accounts for cash-based entries"
                        accent={C.green} accentDark={C.greenDark} accentTint={C.greenTint}
                        icon={<PaidIcon sx={{ fontSize: 17 }} />}
                        rows={CASH_ROWS}
                        colA={cashReceipt} colB={cashPayment}
                        onChangeA={onCashReceipt} onChangeB={onCashPayment}
                        bookTypeA="CBR" bookTypeB="CBP"
                        {...shared}
                    />

                    {/* Info strip */}
                    <Box sx={{ bgcolor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '12px', px: 1.75, py: 1, display: 'flex', alignItems: 'flex-start', gap: 1, flexShrink: 0 }}>
                        <WarningAmberOutlinedIcon sx={{ fontSize: 15, color: '#B45309', mt: 0.1, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.72rem', color: '#78350F', lineHeight: 1.5 }}>
                            Make sure the selected accounts are active and correctly categorised in your chart of accounts.
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* ── Sticky action bar ──────────────────────────────────────────── */}
            <Box sx={{ bgcolor: themeColors.neutral.surface, borderTop: `1px solid ${C.border}`, boxShadow: '0 -3px 12px rgba(15,23,42,0.04)', px: { xs: 1.5, md: 3 }, py: 1.1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', flexShrink: 0, gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 0 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isDirty ? themeColors.warning.main : themeColors.success.main, flexShrink: 0, boxShadow: isDirty ? '0 0 0 3px rgba(245,158,11,0.18)' : '0 0 0 3px rgba(16,185,129,0.16)' }} />
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 600, color: isDirty ? '#B45309' : C.sub }}>
                        {isDirty ? 'You have unsaved changes' : 'All changes saved'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', md: 'auto' } }}>
                    <Button variant="outlined" size="small" onClick={fetchData} disabled={saveLoading}
                        sx={{ height: 34, textTransform: 'none', fontWeight: 700, fontSize: '0.76rem', borderColor: C.borderStrong, color: C.ink, borderRadius: '10px', px: 2, flex: { xs: 1, md: 'initial' }, '&:hover': { borderColor: C.blue, bgcolor: C.surface } }}>
                        {isDirty ? 'Discard' : 'Refresh'}
                    </Button>
                    <Button variant="contained" size="small"
                        startIcon={saveLoading ? <CircularProgress size={12} sx={{ color: themeColors.neutral.surface }} /> : <SaveOutlinedIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={() => setConfirmOpen(true)} disabled={saveLoading || !isDirty}
                        sx={{
                            height: 34, textTransform: 'none', fontWeight: 700, fontSize: '0.76rem',
                            background: gradients.primary,
                            borderRadius: '10px', px: 2.4,
                            boxShadow: '0 6px 16px rgba(24,119,242,0.3)',
                            flex: { xs: 1, md: 'initial' }, whiteSpace: 'nowrap',
                            transition: 'transform .15s, box-shadow .15s',
                            '&:hover': { boxShadow: '0 10px 24px rgba(24,119,242,0.42)', transform: 'translateY(-1px)' },
                            '&.Mui-disabled': { background: '#F0F2F5', color: C.muted, boxShadow: 'none' },
                        }}>
                        {saveLoading ? 'Saving…' : 'Save Configuration'}
                    </Button>
                </Box>
            </Box>

            {/* ── Snackbar ───────────────────────────────────────────────────── */}
            <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '12px', fontWeight: 700, boxShadow: '0 10px 28px rgba(15,23,42,0.2)' }}>{snackbar.message}</Alert>
            </Snackbar>

            {/* ── Confirm dialog ─────────────────────────────────────────────── */}
            <Dialog open={confirmOpen} onClose={() => { if (!saveLoading) setConfirmOpen(false); }} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden', border: `1px solid ${C.border}`, boxShadow: '0 20px 50px rgba(15,23,42,0.18)' } }}>
                {saveLoading
                    ? <LinearProgress sx={{ height: 4, bgcolor: 'rgba(24,119,242,0.15)', '& .MuiLinearProgress-bar': { bgcolor: C.blue } }} />
                    : <Box sx={{ height: 4, background: `linear-gradient(90deg, ${C.blue} 0%, ${C.blueDark} 100%)` }} />}
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: '9px', bgcolor: C.blueTint, color: C.blue, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <SaveOutlinedIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: C.ink }}>Confirm Save</Typography>
                </Box>
                <Divider sx={{ borderColor: C.border }} />
                <Box sx={{ px: 2.5, py: 2 }}>
                    <Typography sx={{ fontSize: '0.84rem', color: '#334155', lineHeight: 1.6 }}>
                        You are about to save the Bank / Cash Posting configuration for{' '}
                        <Box component="span" sx={{ fontWeight: 700, color: C.blue }}>
                            {selectedCompany?.CompanyName || 'this company'}
                        </Box>
                        . This will update all default ledger account mappings.
                    </Typography>
                </Box>
                <Divider sx={{ borderColor: C.border }} />
                <DialogActions sx={{ px: 2.5, py: 1.4, gap: 1 }}>
                    <Button onClick={() => setConfirmOpen(false)} variant="outlined" size="small" disabled={saveLoading}
                        sx={{ height: 34, textTransform: 'none', fontWeight: 700, fontSize: '0.76rem', borderRadius: '10px', borderColor: C.borderStrong, color: C.sub, '&:hover': { borderColor: themeColors.neutral.muted, bgcolor: C.surface } }}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} variant="contained" size="small" disabled={saveLoading}
                        startIcon={saveLoading ? <CircularProgress size={12} sx={{ color: themeColors.neutral.surface }} /> : null}
                        sx={{
                            height: 34, textTransform: 'none', fontWeight: 700, fontSize: '0.76rem', borderRadius: '10px',
                            background: gradients.primary,
                            boxShadow: '0 6px 16px rgba(24,119,242,0.3)',
                            '&:hover': { boxShadow: '0 8px 18px rgba(24,119,242,0.4)' },
                        }}>
                        {saveLoading ? 'Saving…' : 'Confirm & Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
